/**
 * Built-in popup visual templates + the theme override fields a form can layer on top of one.
 * Mirrored (by hand — no shared package, same convention as types.ts) in the dashboard's
 * PopupDesignTab so the editor's live preview and the widget's real render never drift apart on
 * what each template/field actually looks like.
 */
export type PopupTemplateId = 'minimal' | 'bold' | 'rounded' | 'dark';

export type PopupBorderRadius = 'sm' | 'md' | 'lg' | 'pill';

export interface CountdownConfig {
  mode: 'fixed' | 'evergreen';
  /** 'fixed' mode: ISO datetime, one shared deadline for every visitor. */
  deadline?: string;
  /** 'evergreen' mode: minutes from this visitor's own first impression, persisted so a page
   * refresh doesn't reset it. */
  durationMinutes?: number;
  expireAction: 'hide-cta' | 'hide-form' | 'redirect';
  redirectUrl?: string;
  label?: string;
}

export interface WheelSegment {
  id: string;
  label: string;
  color: string;
  /** Relative weight for the spin's weighted-random result — same idea as LeadVariant.weight. */
  weight: number;
  /** An existing commerce.Coupon's code string, revealed as this segment's prize. */
  discountCode?: string;
}

export interface WheelConfig {
  segments: WheelSegment[];
}

/** The freeform `theme` JSONB column, as this renderer understands it. Every field is optional —
 * an empty `{}` (every form's theme before anyone customizes it) still renders correctly via the
 * 'minimal' template's own defaults. */
export interface LeadFormTheme {
  templateId?: PopupTemplateId;
  headline?: string;
  body?: string;
  ctaLabel?: string;
  imageUrl?: string;
  /** Overrides the template's button color. */
  primaryColor?: string;
  /** Overrides the template's panel background. */
  backgroundColor?: string;
  /** Overrides the template's body/heading text color. */
  textColor?: string;
  borderRadius?: PopupBorderRadius;
  countdown?: CountdownConfig;
  /** COUPON_WHEEL only. */
  wheel?: WheelConfig;
  /** RIBBON only. */
  ribbonPosition?: 'top' | 'bottom';
  /** SLIDE_IN / SCROLL_BOX / WIDGET only. */
  corner?: 'bottom-right' | 'bottom-left';
  /** SLIDE_IN / SCROLL_BOX only — the two default footprints render/cornerCard.ts offers. */
  boxStyle?: 'slim' | 'full';
  /** TWO_STEP only — the label on the first-click CTA, before the real form panel opens. */
  stepOneLabel?: string;
  /** WIDGET only — the label on the persistent collapsed corner tab. */
  widgetLabel?: string;
  /** Any display type — configures the recommendations widget; resolved server-side (see
   * LeadRecommendationService), the SDK just fetches whatever this points at. Kept here only so
   * the dashboard's editor can read/write it through the same theme JSON; forms-sdk itself never
   * inspects this shape directly (render/recommendations.ts calls `/recommendations` instead,
   * which already returns resolved product cards, not this spec). */
  recommendations?: {
    mode: 'AUTO' | 'MANUAL';
    contentTypeSlug?: string;
    excludeCurrentSlug?: string;
    productSlugs?: string[];
    count?: number;
    ctaLabel?: string;
  };
}

export interface ResolvedPopupStyle {
  background: string;
  text: string;
  buttonBg: string;
  buttonText: string;
  border: string;
  panelRadius: string;
  buttonRadius: string;
}

const RADIUS_PX: Record<PopupBorderRadius, { panel: string; button: string }> = {
  sm: { panel: '6px', button: '6px' },
  md: { panel: '12px', button: '8px' },
  lg: { panel: '20px', button: '12px' },
  pill: { panel: '20px', button: '999px' },
};

const TEMPLATES: Record<PopupTemplateId, ResolvedPopupStyle & { defaultRadius: PopupBorderRadius }> = {
  minimal: {
    background: '#ffffff',
    text: '#16181d',
    buttonBg: '#16181d',
    buttonText: '#ffffff',
    border: '1px solid #e5e7eb',
    defaultRadius: 'md',
    panelRadius: RADIUS_PX.md.panel,
    buttonRadius: RADIUS_PX.md.button,
  },
  bold: {
    background: '#4f46e5',
    text: '#ffffff',
    buttonBg: '#ffffff',
    buttonText: '#4f46e5',
    border: 'none',
    defaultRadius: 'lg',
    panelRadius: RADIUS_PX.lg.panel,
    buttonRadius: RADIUS_PX.lg.button,
  },
  rounded: {
    background: '#fff7ed',
    text: '#7c2d12',
    buttonBg: '#f97316',
    buttonText: '#ffffff',
    border: 'none',
    defaultRadius: 'pill',
    panelRadius: RADIUS_PX.pill.panel,
    buttonRadius: RADIUS_PX.pill.button,
  },
  dark: {
    background: '#111827',
    text: '#f9fafb',
    buttonBg: '#22d3ee',
    buttonText: '#111827',
    border: '1px solid #374151',
    defaultRadius: 'md',
    panelRadius: RADIUS_PX.md.panel,
    buttonRadius: RADIUS_PX.md.button,
  },
};

export const TEMPLATE_IDS = Object.keys(TEMPLATES) as PopupTemplateId[];

/** Applies a form's theme overrides on top of its chosen (or default) template's base style. */
export function resolvePopupStyle(theme: LeadFormTheme): ResolvedPopupStyle {
  const base = TEMPLATES[theme.templateId ?? 'minimal'] ?? TEMPLATES.minimal;
  const radius = theme.borderRadius ? RADIUS_PX[theme.borderRadius] : null;

  return {
    background: theme.backgroundColor || base.background,
    text: theme.textColor || base.text,
    buttonBg: theme.primaryColor || base.buttonBg,
    buttonText: base.buttonText,
    border: base.border,
    panelRadius: radius?.panel ?? base.panelRadius,
    buttonRadius: radius?.button ?? base.buttonRadius,
  };
}
